import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useTheme } from '../../../../lib/hooks'

import Button from '@/components/Button'

import useCheckout from '../../hooks/useCheckout'
import Step from '../Step/Step'
import { CheckoutAddon, ConfirmationContent, ConfirmationIcon } from './Confirmation.styles'
import ConfirmationEInvoiceModal from './ConfirmationEInvoiceModal'

const Confirmation = () => {
  const { t } = useTranslation()
  const { cart, reset, paymentError, setCurrentStep, setPaymentError, setPaymentStatus } = useCheckout()
  const navigate = useNavigate()
  const themes = useTheme()
  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const [isEInvoiceLoading, setIsEInvoiceLoading] = useState(false)
  const [eInvoiceQR, setEInvoiceQR] = useState(null)

  const resetCheckout = () => {
    reset()
    navigate('/')
  }

  const getEInvoice = () => {
    setIsEInvoiceLoading(true)
    setTimeout(() => {
      setIsEInvoiceLoading(false)
      // Sample QR code data - replace with actual API call
      setEInvoiceQR('iVBORw0KGgoAAAANSUhEUgAAASwAAAEsAQAAAABRBrPYAAACQElEQVR4Xu2XQY7bMAxF6ZWPoZta8k1zhC69kvrfl6edUQu0m6IIYcIwaPI5QD4pMonxN/Yt1shv7cEWe7DFHmyxB1vswRb7F1gPrF5FTh2vesmP2F9E91wY/uvYRyP+06l3KhP2IkvK4vgtKaPIkRGrV2zIYj81RkS9HVEVukqzROkw3RQs5xhcVxk40mSmEmHIoO9O3T9fRHNh03qUTquXk7qPZmBaGqxT9Nc8y/Wuvlqdzt9yYcMKqM83Zw9LYXFi+mkwRTYiAPgDoJK5X8mDRdFdmqi9XfcyrtL3H0IlwnbvnYj6BaDh8RNhbm+y3fuoBXXvEVs+7JIO2jtMqtne57AaDO18mCO6B36TGpcu9EmHaXCp6MXPzCvIr4MrAdbtd8/n8PjqFF0Mj6mwYEwdXMPK8FO5M8rWSf72GFkUqN5EzW/ha6CRz4MNRvTcRIggEtj68AmZMIKKEJfT6HAeq894JqyTVWOjxrAaDlL3lguTIULIkAKMnr9TybDTS3b6E2hB2y+CvD82d5BaWudaKbWBNFnXbgKsfzCBCOhwDs7y9stxfndsqKXd3gPGPeAmPwdXJqx/FFoNIP90xTndO8s3EzbNcSbY6T6fr/TPpX9/DMCbqEsBt8Hwf73DWzgVhg9w+pUjOMUdQZAoF6ZCz9/GOs5lRg4z/oRs2GFZSBmQNeIJsToAqs5ylD67PSGmG7XevGc1uDy7SE3LgwXm0rN5I9Ck0QM3nAb7oz3YYg+22IMt9mCLPdhi/wf7DpcVjSOTO3NZAAAAAElFTkSuQmCC')
    }, 2000)
  }

  const printConfirmation = () => {
    setIsPrintLoading(true)
    setTimeout(() => {
      setIsPrintLoading(false)
    }, 2000)
  }

  const handleErrorBack = () => {
    setCurrentStep('PAYMENT')
    setPaymentError(null)
    setPaymentStatus('DEFAULT')
  }

  return (
    <Step>
      <ConfirmationContent>
        {/* Success */}
        {!paymentError && (
          <>
            <ConfirmationIcon>
              <svg width="200" height="200" fill="none" xmlns="http://www.w3.org/2000/svg">
                <motion.path
                  d="M175.98 38.9C158.21 16.7 131 2.5 100 2.5C46.21 2.5 2.5 46.21 2.5 100C2.5 153.79 46.21 197.5 100 197.5C153.79 197.5 197.5 153.79 197.5 100C197.5 85.15 194.21 70.9 188.2 58.3"
                  stroke={themes.colors.global}
                  strokeWidth="5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    delay: 0.25,
                    duration: 0.75
                  }}
                />
                <motion.path
                  d="M42.9 82.8L95.5 135.4L197.5 33.6"
                  stroke={themes.colors.primary}
                  strokeWidth="5"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{
                    delay: 0.75,
                    duration: 0.75
                  }}
                />
              </svg>
            </ConfirmationIcon>
            <h1>{t('paymentConfirmationTitle')}</h1>
            <CheckoutAddon>
              <h3>{t('paymentReceiptQuestion')}</h3>
              <Button block onClick={() => printConfirmation()} isLoading={isPrintLoading}>
                {t('printConfirmation')}
              </Button>
              <Button block onClick={() => getEInvoice()} isLoading={isEInvoiceLoading} variant="secondary">
                {t('getEInvoice')}
              </Button>
              <Button block variant="secondary" onClick={() => resetCheckout()}>
                {t('skipAndFinish')}
              </Button>
            </CheckoutAddon>
          </>
        )}
        {/* Error */}
        {paymentError && (
          <>
            <ConfirmationIcon>
              <svg viewBox="0 0 138 139">
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(4.208529, 4.000000)" stroke="#001489" strokeWidth="3">
                    <motion.line
                      x1="0"
                      y1="130.073547"
                      x2="130.073547"
                      y2="0"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{
                        delay: 0.25,
                        duration: 0.75
                      }}
                    ></motion.line>
                    <motion.line
                      x1="0"
                      y1="130.073547"
                      x2="130.073547"
                      y2="0"
                      transform="translate(65.036774, 65.036774) scale(-1, 1) translate(-65.036774, -65.036774) "
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{
                        delay: 0.75,
                        duration: 0.75
                      }}
                    ></motion.line>
                  </g>
                </g>
              </svg>
            </ConfirmationIcon>
            <h1>{t('paymentErrorTitle')}</h1>
            <p>{t('paymentErrorDescription')}</p>
            <Button size="lg" variant="secondary" onClick={handleErrorBack}>
              {t('back')}
            </Button>
          </>
        )}
      </ConfirmationContent>
      <AnimatePresence>
        {eInvoiceQR && <ConfirmationEInvoiceModal image={eInvoiceQR} onClose={() => setEInvoiceQR(null)} />}
      </AnimatePresence>
    </Step>
  )
}

export default Confirmation
